<!--
 * @Author: AI Assistant
 * @Date: 2025-12-30
 * @Description: 音乐生成设置面板组件
 * @FilePath: /weryai-web/components/Tool/Text-to-Music-Setting.vue
-->
<template>
    <div
        class="flex flex-col justify-between bg-setting-bg max-h-[inherit] w-full overflow-y-auto rounded-lg p-4 lg:p-6 lg:pb-0 lg:!w-[424px] lg:border-r lg:border-r-[#1D1D20]"
    >
        <div>
            <!-- 模式切换（人声歌曲/纯音乐） -->
            <tool-music-mode-switch
                v-model:mode="mode"
            ></tool-music-mode-switch>

            <!-- 创意描述与风格标签 -->
            <tool-prompt-with-tags
                v-model:prompt="description"
                v-model:styles="styles"
                v-model:inputText="inputText"
            ></tool-prompt-with-tags>

            <!-- 歌词输入（仅人声模式） -->
            <tool-lyrics-input
                v-if="mode === 'vocal'"
                v-model:lyrics="lyrics"
                :placeholder="t('menus.textToMusicPage.lyricsPlaceholder')"
            ></tool-lyrics-input>

            <!-- 参考音频 -->
            <tool-reference-audio
                v-model:audioFile="referenceAudio"
                v-model:audioUrl="referenceAudioUrl"
                v-model:audioName="referenceAudioName"
            ></tool-reference-audio>

            <!-- 音色选择（仅人声模式） -->
            <tool-gender-select
                v-if="mode === 'vocal'"
                v-model:gender="gender"
            ></tool-gender-select>

            <!-- 公开可见性 -->
            <tool-public-visibility
                v-model:public_visible="public_visible"
            ></tool-public-visibility>

            <!-- 版权保护 -->
            <tool-copyright-protection
                v-model:copyright_protection="copyright_protection"
            ></tool-copyright-protection>
        </div>

        <!-- 生成按钮 -->
        <tool-credit-required
            class="lg:sticky lg:bottom-0 lg:py-4 lg:!mb-0 bg-setting-bg"
            :type="props.type"
            :create_btn_disable="props.create_btn_disable"
            workflowType="music_creation_generation"
            :m="mode"
            :loading="props.create_loading"
        ></tool-credit-required>
    </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// Props
const props = withDefaults(
    defineProps<{
        type: any
        create_btn_disable: boolean
        create_loading: boolean
    }>(),
    {
        type: 'text_to_music',
        create_btn_disable: false,
        create_loading: false,
    }
)

// Emits
const emit = defineEmits<{
    (e: 'update:formData', data: MusicFormData): void
}>()

// 表单数据类型（字段名与接口保持一致）
interface MusicFormData {
    mode: 'vocal' | 'inst'
    description: string
    styles: Record<string, string>
    lyrics: string
    referenceAudio: File | null
    gender: 'm' | 'f'
    public_visible: boolean
    copyright_protection: boolean
}

// ========== 表单状态 ==========

// 模式切换
const mode = defineModel<'vocal' | 'inst'>('mode', {
    default: 'vocal',
})

// 创意描述
const description = defineModel('description', {
    type: String,
    default: '',
})

// 风格标签（Map 格式：key 枚举值，value 中文名称）
const styles = defineModel('styles', {
    type: Object as () => Record<string, string>,
    default: () => ({}),
})

// 歌词
const lyrics = defineModel('lyrics', {
    type: String,
    default: '',
})

// 参考音频
const referenceAudio = defineModel<File | null>('referenceAudio', {
    default: null,
})

// 参考音频 URL（上传到 S3 后的地址）
const referenceAudioUrl = defineModel<string>('referenceAudioUrl', {
    default: '',
})

// 参考音频名称
const referenceAudioName = defineModel<string>('referenceAudioName', {
    default: '',
})

// 音色
const gender = defineModel<'m' | 'f'>('gender', {
    default: 'm',
})

// 公开可见性
const public_visible = defineModel('public_visible', {
    type: Boolean,
    default: true,
})

// 版权保护
const copyright_protection = defineModel('copyright_protection', {
    type: Boolean,
    default: true,
})

// 用户输入的纯文本（透传给子组件，用于页面层验证）
const inputText = defineModel<string>('inputText', {
    default: '',
})

// 监听表单数据变化，向上传递
watch(
    () => ({
        mode: mode.value,
        description: description.value,
        styles: styles.value,
        lyrics: lyrics.value,
        referenceAudio: referenceAudio.value,
        gender: gender.value,
        public_visible: public_visible.value,
        copyright_protection: copyright_protection.value,
    }),
    newData => {
        emit('update:formData', newData as MusicFormData)
    },
    { deep: true }
)

// 模式切换时的逻辑
watch(mode, newMode => {
    // 切换到纯音乐模式时，清空歌词
    if (newMode === 'inst') {
        lyrics.value = ''
    }
})
</script>

<style scoped>
/* 组件样式已通过 Tailwind 内联实现 */
</style>
